import { API_BASE_URL } from '../../../../../helpers/common';
import type { AiClientContext, AiResourceTag } from '../contracts/ai-context.types';
import type { AiPromptSelection } from '../contracts/ai-prompt.types';
import type {
  PresentationDocumentMetaEvent,
  PresentationNodeEvent,
} from '../contracts/ai-presentation.types';
import {
  AI_STREAM_IDLE_TIMEOUT_MS,
  AI_STREAM_RETRY_DELAYS_MS,
} from '../workspace/workspaceLayout.constants';

const streamBaseUrl = String(API_BASE_URL || '').replace(/\/+$/, '');

export type AiConversationStreamEvent =
  | {
      type: 'assistant.created';
      message: {
        id: number;
        conversationId: number;
        role: 'assistant';
        content: '';
        createdAt: string;
      };
    }
  | { type: 'assistant.delta'; messageId: number; delta: string }
  | {
      type: 'assistant.completed';
      message: {
        id: number;
        conversationId: number;
        role: 'assistant';
        content: string;
        createdAt: string;
      };
    }
  | { type: 'context.warning'; message: string }
  | {
      type: 'tool.started' | 'tool.progress' | 'tool.completed';
      messageId?: number;
      toolCallId?: string;
      tool?: string;
      message?: string;
      durationMs?: number;
      cacheHit?: boolean;
    }
  | {
      type: 'tool.failed';
      messageId?: number;
      toolCallId?: string;
      tool?: string;
      message?: string;
      error?: string;
    }
  | {
      type: 'run.failed';
      messageId: number;
      error: string;
      errorCode?: 'provider_unavailable';
    }
  | PresentationNodeEvent
  | PresentationDocumentMetaEvent;

interface StreamAiConversationMessageOptions {
  conversationId: number;
  content: string;
  promptId?: AiPromptSelection['id'];
  promptVersion?: AiPromptSelection['version'];
  resourceTags?: Pick<AiResourceTag, 'kind' | 'id'>[];
  /** Closes the dead-contract gap — the backend already accepts this field. */
  clientContext?: AiClientContext;
  accessToken: string | undefined;
  signal: AbortSignal;
  onEvent: (event: AiConversationStreamEvent) => void;
}

/**
 * Reads an SSE stream until it ends, calling `onEvent` for each parsed frame.
 * Throws on network-level errors (after exhausting retries) or idle timeout.
 * 4xx/5xx responses throw immediately — retrying a bad request is wrong.
 */
const readStream = async (
  response: Response,
  signal: AbortSignal,
  onEvent: (event: AiConversationStreamEvent) => void,
): Promise<void> => {
  if (!response.body) throw new Error('Unable to start AI response stream');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const resetIdle = (): ReturnType<typeof setTimeout> =>
    setTimeout(() => {
      reader.cancel();
    }, AI_STREAM_IDLE_TIMEOUT_MS);

  let idleTimer = resetIdle();

  try {
    while (true) {
      if (signal.aborted) break;

      const result = await reader.read();
      clearTimeout(idleTimer);

      if (result.done) break;

      idleTimer = resetIdle();
      buffer += decoder.decode(result.value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        const data = frame
          .split('\n')
          .find((line) => line.startsWith('data: '))
          ?.slice('data: '.length);

        if (data) {
          onEvent(JSON.parse(data) as AiConversationStreamEvent);
        }
      }
    }
  } finally {
    clearTimeout(idleTimer);
    reader.releaseLock();
  }
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const streamAiConversationMessage = async ({
  conversationId,
  content,
  promptId,
  promptVersion,
  resourceTags,
  clientContext,
  accessToken,
  signal,
  onEvent,
}: StreamAiConversationMessageOptions): Promise<void> => {
  const url = `${streamBaseUrl}/ai/conversations/${conversationId}/messages/stream`;
  const body = JSON.stringify({
    content,
    ...(promptId ? { promptId } : {}),
    ...(promptVersion ? { promptVersion } : {}),
    ...(resourceTags?.length ? { resourceTags } : {}),
    ...(clientContext ? { context: clientContext } : {}),
  });
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Content-Type': 'application/json',
    ...(clientContext?.locale
      ? { 'Accept-Language': clientContext.locale }
      : {}),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };

  const delays = [...AI_STREAM_RETRY_DELAYS_MS];
  let lastError: unknown;

  for (let attempt = 0; attempt <= delays.length; attempt++) {
    if (signal.aborted) return;

    if (attempt > 0) {
      await sleep(delays[attempt - 1]);
      if (signal.aborted) return;
    }

    let response: Response;
    try {
      response = await fetch(url, { method: 'POST', headers, body, signal });
    } catch (fetchError) {
      // AbortError: user cancelled — don't retry, surface immediately.
      if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
        throw fetchError;
      }
      lastError = fetchError;
      // Network failure — retry with backoff (only if attempts remain).
      continue;
    }

    // 4xx/5xx: server rejected the request — never retry.
    if (!response.ok) {
      throw new Error(`AI stream responded with HTTP ${response.status}`);
    }

    // Successful connection: read until done (may throw on idle timeout).
    try {
      await readStream(response, signal, onEvent);
      return; // success — exit the retry loop
    } catch (streamError) {
      if (streamError instanceof DOMException && streamError.name === 'AbortError') {
        throw streamError;
      }
      lastError = streamError;
      // Stream broke mid-flight — retry if attempts remain.
    }
  }

  throw lastError ?? new Error('AI stream failed after all retry attempts');
};
